import { useDateTimeDisplay } from '../../util/useDateTimeDisplay';
import { BILLING_PORTAL } from "../../graphql/stripePayments";
import { getPricingClosePath } from "../../util/upgrade/pricingNavigation";
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, alpha } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useApolloClient, useQuery, useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import jwtDecode from "jwt-decode";

import {
  GET_PLANS,
  GET_PLANS_LEGACY,
  GET_BILLING_SUBSCRIPTION,
  UPDATE_STRIPE_SEATS,
  CREATE_STRIPE_SUBSCRIPTION,
  GET_TOKEN,
  GET_BUSINESS_CATEGORIES,
} from "../../graphql/queries";
import { UserContext } from "../../components/auth/auth-context";
import { useSelectBusinesses, useSelectedBusiness } from "../../redux/store";
import { setSelectedBusiness } from "../../redux/actions/businessActions";
import { UPDATE_DEFAULT_BUSINESS } from "../../graphql/mutations";
import ContactUsForm from "./contactUsForm";
import { UPDATE_BUSINESS_BASIC } from "../../graphql/mutations/business";
import {
  hasCurrentProductSubscription,
  hasMultiplePrimaryProductSubscriptions,
  isProductSubscribed,
} from "../../../core/business/productAccess";
import { normalizeProductKey } from "../../../core/business/productCatalog";
import { getTrialDaysLeft } from "../../util/utils";
import { StaffStatus } from "../../util/utils";
import { getBrandVertical } from "../../util/brandDomains";

/* ================= PRICING CONSTANTS ================= */

const PLAN_CARD_BORDER_BY_CODE = {
  FREE: "#94a3b8",
  STANDARD: "#2563eb",
  PREMIUM: "#059669",
  ENTERPRISE: "#d97706",
};
const PLAN_CARD_BG_BY_CODE = {
  FREE: "linear-gradient(180deg, #f8fafc 0%, #ffffff 26%)",
  STANDARD: "linear-gradient(180deg, #eff6ff 0%, #ffffff 26%)",
  PREMIUM: "linear-gradient(180deg, #ecfdf5 0%, #ffffff 26%)",
  ENTERPRISE: "linear-gradient(180deg, #fff7ed 0%, #ffffff 26%)",
};
const MIN_INITIAL_SEATS = 0;
const DEFAULT_INITIAL_SEATS = 0;

/* ================= STYLES ================= */

const GridLayout = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const Card = styled(Box)(({ theme, $planCode, $currentPlan }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(180deg, ${alpha(PLAN_CARD_BORDER_BY_CODE[$planCode] || theme.palette.primary.main, 0.14)} 0%, transparent 26%), ${theme.palette.background.paper}`
    : PLAN_CARD_BG_BY_CODE[$planCode] || theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `${$currentPlan ? 3 : 2}px solid ${theme.palette.mode === 'dark'
      ? alpha(PLAN_CARD_BORDER_BY_CODE[$planCode] || theme.palette.primary.main, $currentPlan ? 0.9 : 0.5)
      : PLAN_CARD_BORDER_BY_CODE[$planCode] || theme.palette.divider
    }`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  boxShadow: $currentPlan
    ? "0 12px 28px rgba(15, 23, 42, 0.14)"
    : "0 6px 16px rgba(15, 23, 42, 0.06)",
}));

const FeatureItem = styled("li")({
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 8,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 700,
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
}));

/* ================= HELPERS ================= */

const FEATURE_LABELS = {
  resident: "Resident & Care Management",
  staffing: "Staffing, Profiles & Care Provider Jobs",
  referrals: "Referrals & Visibility",
  booking: "Scheduling, Booking & Follow-Ups",
  team: "Team Scheduling",
  admin: "Admin Controls",
  analytics: "Analytics & Reporting",
  operations: "Operations & Workforce",
  compliance: "Compliance & Documentation",
  integrations: "Integrations & APIs",
  automation: "Automation, Reminders & Alerts",
  notifications: "Notifications & Alerts",
  ai: "AI & Intelligence",
  capture: "Capture & Calendar Reminders",
};

const FEATURE_SECTION_ORDER = [
  "resident",
  "automation",
  "ai",
  "booking",
  "capture",
  "team",
  "admin",
  "analytics",
  "staffing",
  "referrals",
  "operations",
  "compliance",
  "integrations",
  "notifications",
];

function normalizeFeatures(raw) {
  if (!raw) return {};
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
}

function getOrderedFeatureEntries(features) {
  const entries = Object.entries(features || {});
  const rank = new Map(FEATURE_SECTION_ORDER.map((key, index) => [key, index]));

  return entries.sort(([left], [right]) => {
    const leftRank = rank.has(left) ? rank.get(left) : Number.MAX_SAFE_INTEGER;
    const rightRank = rank.has(right) ? rank.get(right) : Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(left).localeCompare(String(right));
  });
}

function formatFeatureText(item) {
  if (!item) return "";
  return String(item).trim();
}

function getPlanTierCode(rawCode) {
  const code = String(rawCode || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return code.replace("BOOKING_", "");
  if (code.startsWith("CARE_")) return code.replace("CARE_", "");
  if (code.startsWith("CAREJOBS_")) return code.replace("CAREJOBS_", "");
  return code;
}

function inferPlanProductKey(plan) {
  const explicitProductKey = normalizeProductKey(plan?.productKey);
  if (explicitProductKey) return explicitProductKey;

  const code = String(plan?.code || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return "booking";
  if (code.startsWith("CARE_")) return "care";
  if (code.startsWith("CAREJOBS_")) return "carejobs_applicant";
  if (code.startsWith("AIVOICE_") || code.startsWith("AI_VOICE_")) return "aivoice";
  return "";
}

function getPlanActionLabel(currentPlanCode, targetPlanCode) {
  const current = String(currentPlanCode || "").toUpperCase();
  const target = String(targetPlanCode || "").toUpperCase();
  if (!target) return "Switch Plan";

  if (target === "STANDARD") {
    if (current === "PREMIUM" || current === "ENTERPRISE") return "Downgrade to Standard";
    if (current === "STANDARD") return "Current Plan";
    return "Upgrade to Standard";
  }

  if (target === "PREMIUM") {
    if (current === "PREMIUM") return "Current Plan";
    return "Upgrade to Premium";
  }

  if (target === "FREE") {
    return "Switch to Free";
  }

  return "Switch Plan";
}

function shouldHidePlanSummary(plan) {
  const code = String(plan?.code || "").trim().toUpperCase();
  return code === "BOOKING_STANDARD" || code === "BOOKING_PREMIUM";
}

function getBookingCaptureFeatures(tierCode) {
  const tier = String(tierCode || "").trim().toUpperCase();
  const scanLimitText = {
    FREE: "5 Capture scans per month",
    STANDARD: "50 Capture scans per month",
    PREMIUM: "250 Capture scans per month",
    ENTERPRISE: "Unlimited Capture scans",
  }[tier] || "Capture scans included";

  return [
    scanLimitText,
    "Scan event posters, flyers, screenshots, business cards, and address images",
    "Extract event details, contacts, addresses, dates, times, and locations",
    "Create calendar drafts from captured poster events with reminder options",
  ];
}

function getDisplayedPlanName(plan, productContext) {
  const rawName = String(plan?.name || "").trim();
  if (!rawName) return "";
  if (productContext !== "booking") return rawName;

  return rawName.replace(/^booking\s+/i, "").trim();
}

function isPricingRoute(path = "") {
  const pathname = String(path || "").split("?")[0].split("#")[0];
  return pathname === "/biz/pricing";
}

function getPricingBrandProductContext() {
  return getBrandVertical();
}

/* ================= COMPONENT ================= */

export default function PricingPage() {
  const { dateLabel } = useDateTimeDisplay();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:800px)");
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const { providerUser, updateProviderUser } = useContext(UserContext);
  const isLoggedIn = Boolean(providerUser?.userId);
  const authReturnTo = `${location.pathname}${location.search}${location.hash}`;
  const loginPath = `/login?returnTo=${encodeURIComponent(authReturnTo)}`;
  const signupPath = `/signup?returnTo=${encodeURIComponent(authReturnTo)}`;

  const businesses = useSelectBusinesses();
  const selectedBusiness = useSelectedBusiness();
  const defaultBusinessId = Number(providerUser?.defaultBusinessId || 0);
  const businessId = Number(
    selectedBusiness?.businessBasic?.id || defaultBusinessId || 0
  ) || null;
  const selectedBusinessName = selectedBusiness?.businessBasic?.name || "";
  const isPersonalWorkspace = selectedBusiness?.businessBasic?.workspaceType === "PERSONAL";

  const [desiredSeats, setDesiredSeats] = useState(DEFAULT_INITIAL_SEATS);
  const [updatingSeats, setUpdatingSeats] = useState(false);
  const [seatToast, setSeatToast] = useState(null);
  const [inlineBotOccupiedWidth, setInlineBotOccupiedWidth] = useState(0);
  const [openDemoDialog, setOpenDemoDialog] = useState(false);
  const productParam = String(
    new URLSearchParams(location.search).get("product") || ""
  ).toLowerCase();
  const verticalParam = String(
    new URLSearchParams(location.search).get("v") || ""
  ).toLowerCase();
  const requestedProductKey = normalizeProductKey(productParam);
  const requestedVerticalKey = normalizeProductKey(verticalParam);
  const brandProductContext = getPricingBrandProductContext();
  const isBookingSubscribed = isProductSubscribed(providerUser, "booking");
  const isCareSubscribed = isProductSubscribed(providerUser, "care");
  const canSelectPricingProduct =
    false;
  const requestedProductContext =
    requestedProductKey === "booking" || requestedProductKey === "care"
      ? requestedProductKey
      : requestedVerticalKey === "booking" || requestedVerticalKey === "care"
        ? requestedVerticalKey
        : "";

  const {
    data: plansData,
    loading: plansLoading,
    error: plansError,
  } = useQuery(GET_PLANS, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
  });
  const {
    data: legacyPlansData,
    loading: legacyPlansLoading,
  } = useQuery(GET_PLANS_LEGACY, {
    skip: !plansError,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES, {
    fetchPolicy: "cache-first",
  });

  const [createSubscription, { loading: subscribing }] = useMutation(
    CREATE_STRIPE_SUBSCRIPTION,
  );
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const [updateBusinessBasic] = useMutation(UPDATE_BUSINESS_BASIC);

  const [openBillingPortal, { loading: openingPortal, error: portalError }] = useMutation(BILLING_PORTAL);
  const [updateStripeSeats] = useMutation(UPDATE_STRIPE_SEATS);

  const getUpgradeCallerPath = () => {
    if (typeof window === "undefined") return "";
    try {
      const raw = localStorage.getItem("upgradeCallerNavigation");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      const path = typeof parsed?.path === "string" ? parsed.path : "";
      const createdAt = Number(parsed?.createdAt || 0);
      const isFresh =
        createdAt > 0 && Date.now() - createdAt < 2 * 60 * 60 * 1000;
      return isFresh && path ? path : "";
    } catch (_error) {
      return "";
    }
  };

  const selectedCategory = (categoriesData?.getBusinessCategories || []).find(
    (category) =>
      Number(category?.id) ===
      Number(selectedBusiness?.businessBasic?.businessCategoryId)
  );
  const categoryName = String(selectedCategory?.name || "");
  const hasCareLicense = Boolean(
    selectedBusiness?.careSettings?.afhLicenseNumber ||
    selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber
  );
  const hasResidents = Array.isArray(selectedBusiness?.residents) && selectedBusiness.residents.length > 0;
  const isCareBusiness =
    hasCareLicense ||
    hasResidents ||
    /adult\s*family\s*home|independent\s*living|assisted\s*living|memory\s*care|nursing\s*home/i.test(
      categoryName
    );
  const productContext =
    "booking";
  const { data: subData, refetch } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: {
      businessId,
      productKey: productContext,
    },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const subscription = subData?.getBillingSubscription;
  const effectivePlansData = Array.isArray(plansData?.plans)
    ? plansData
    : Array.isArray(legacyPlansData?.plans)
      ? legacyPlansData
      : null;
  const hasResolvedPlansArray = Array.isArray(effectivePlansData?.plans);
  const allPlans = hasResolvedPlansArray ? effectivePlansData.plans : [];
  const backendPlans = allPlans.filter((plan) => {
    const planProductKey = inferPlanProductKey(plan);
    if (planProductKey) return planProductKey === productContext;

    const code = String(plan?.code || "").trim().toUpperCase();
    const name = String(plan?.name || "").trim().toLowerCase();
    {
      return code.startsWith("BOOKING_") || name.includes("booking");
    }

    return !code.startsWith("BOOKING_") && !name.includes("booking");
  });
  const availablePlans = backendPlans;
  const freePlan = availablePlans.find((plan) => getPlanTierCode(plan.code) === "FREE");
  const freePlanId = freePlan?.id || null;
  const hasConfigurablePricing = availablePlans.length > 0;
  const hasPurchasablePlans = availablePlans.some(
    (plan) => String(plan?.purchaseState || "available").toLowerCase() === "available"
  );
  const hasPaidPlans = Boolean(
    availablePlans.some((plan) => getPlanTierCode(plan?.code) !== "FREE")
  );
  const pricingCatalogLoading =
    (plansLoading && !plansError) ||
    legacyPlansLoading ||
    !hasResolvedPlansArray;

  useEffect(() => {
    if (!Array.isArray(businesses) || businesses.length === 0) return;
    if (selectedBusiness?.businessBasic?.id) return;
    const fallbackBusiness =
      businesses.find(
        (business) =>
          Number(business?.businessBasic?.id) === Number(defaultBusinessId)
      ) || businesses[0];
    const fallbackId = Number(fallbackBusiness?.businessBasic?.id || 0);
    if (fallbackId > 0) {
      dispatch(setSelectedBusiness(fallbackId));
    }
  }, [businesses, defaultBusinessId, selectedBusiness, dispatch]);

  /* ================= STATUS ================= */

  const status = subscription?.status || "none";
  const isActive = status === "active";
  const isCanceling = status === "canceling";
  const isCanceled = status === "canceled";
  const isTrialing = status === "trialing";
  const isCurrentProductSubscribed = hasCurrentProductSubscription(
    providerUser,
    productContext,
    subscription
  );
  const trialDaysLeft = getTrialDaysLeft(providerUser || {});
  const isBusinessTrial =
    isTrialing ||
    trialDaysLeft === "Unlimited" ||
    Number(trialDaysLeft) > 0;
  const canManageCurrentSeats =
    (isActive && !isCanceling) || isBusinessTrial;
  const canPersistSeatChanges = isActive && !isCanceling;
  const hasCurrentPlanForUi = Boolean(subscription?.planId) && !isCanceled;

  const currentPlanId =
    hasCurrentPlanForUi
      ? String(subscription?.planId)
      : isBookingSubscribed && freePlanId
        ? String(freePlanId)
        : (isCanceled || status === "none") && freePlanId
          ? String(freePlanId)
          : null;
  const currentPlan = plansData?.plans?.find(
    (plan) => String(plan.id) === String(currentPlanId)
  );
  const currentPlanCode =
    currentPlan?.code
      ? getPlanTierCode(currentPlan.code)
      : ((status === "none" || isCanceled) ? "FREE" : null);
  const currentPlanRawCode = String(currentPlan?.code || "").toUpperCase();
  const isBookingSubscriptionPlan = currentPlanRawCode.startsWith("BOOKING_");
  const quantityMetric =
    "staff";
  const quantityPanelTitle =
    "Workspace members";
  const quantityUnitLabel =
    "workspace member";
  const quantityUsedLabel =
    "active members";
  const zeroQuantityLabel =
    "workspace member seats";
  const isCurrentPaidPlan =
    currentPlanCode === "STANDARD" ||
    currentPlanCode === "PREMIUM" ||
    currentPlanCode === "ENTERPRISE";
  const nextBillingDateLabel = subscription?.nextBillingAt
    ? dateLabel(subscription.nextBillingAt)
    : "";
  const localResidentCount =
    0;
  const localStaffCount =
    Number(
        (selectedBusiness?.businessStaffs || []).filter(
          (staff) => Number(staff?.status) === Number(StaffStatus.ACTIVE)
        ).length ?? 0
      );
  const localUsageCount =
    localStaffCount;
  const subscribedPaidQuantity = Number(subscription?.residentSeats ?? 0);
  const subscribedUsedQuantity =
    localStaffCount;

  const paidSeats =
    ((isActive || isCanceling) && (isBookingSubscriptionPlan))
      ? subscribedPaidQuantity
      : isBusinessTrial
        ? Math.max(
          subscribedPaidQuantity,
          localUsageCount
        )
        : 0;

  const usedSeats =
    ((isActive || isCanceling) && (isBookingSubscriptionPlan))
      ? subscribedUsedQuantity
      : isBusinessTrial
        ? Math.max(
          subscribedUsedQuantity,
          localUsageCount
        )
        : localStaffCount;

  const displayedSeatLimit =
    isBusinessTrial
      ? Math.max(paidSeats, usedSeats)
      : canManageCurrentSeats && Number(desiredSeats) >= 0
        ? Number(desiredSeats)
        : paidSeats;
  const displayedSeatsLeft = Math.max(0, displayedSeatLimit - usedSeats);
  const minSeatsAllowed = 0;
  const disableSeatControlsForTrial = isBusinessTrial && !canPersistSeatChanges;
  const shouldReserveDockedBotSpace = inlineBotOccupiedWidth > 0;
  const isShowingCurrentDefaultBusiness =
    Number(defaultBusinessId || 0) > 0 &&
    Number(defaultBusinessId || 0) === Number(businessId || 0);
  const pricingEyebrow =
    "Booking Product Pricing";
  const pricingTitle =
    "Pricing for standalone booking";
  const pricingDescription =
    "Compare Booking plans for event types, host pages, reminders, availability, and scheduling workflows.";
  const pricingFootnote = pricingCatalogLoading
    ? "Loading pricing plans from the backend catalog..."
    : !hasConfigurablePricing
      ? "Booking pricing is not configured yet. The billing flow is now product-ready, so standalone Booking plans can be added in Stripe and exposed here without another UI rewrite."
      : !hasPurchasablePlans
        ? "This product has plan records, but none are currently marked available for checkout."
        : "This workspace is using the standalone Booking product path.";

  useEffect(() => {
    if (isActive || isCanceling) {
      setDesiredSeats(paidSeats);
      return;
    }
    if (isBusinessTrial) {
      setDesiredSeats(Math.max(paidSeats, usedSeats));
      return;
    }
    setDesiredSeats(0);
  }, [isActive, isCanceling, isBusinessTrial, paidSeats, usedSeats, businessId]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const initial = Number(window.__planybotInlineOccupiedWidth || 0);
      setInlineBotOccupiedWidth(initial > 0 ? initial : 0);
    }

    const handleInlineBotPanelChange = (event) => {
      const width = Number(event?.detail?.occupiedWidth || 0);
      setInlineBotOccupiedWidth(width > 0 ? width : 0);
    };

    window.addEventListener(
      "planybot:inline-panel-change",
      handleInlineBotPanelChange,
    );

    return () => {
      window.removeEventListener(
        "planybot:inline-panel-change",
        handleInlineBotPanelChange,
      );
    };
  }, []);

  /* ================= ACTIONS ================= */

  const updateProductSubscription = async (productId, subscribed) => {
    if (!businessId) {
      throw new Error("Missing workspace. Please refresh and try again.");
    }

    const subscribedProducts = {
      care: isCareSubscribed,
      booking: isBookingSubscribed,
      [productId]: subscribed,
    };

    await updateBusinessBasic({
      variables: {
        input: {
          id: businessId,
          additionalInfo: {
            subscribedProducts,
            workspaceProducts: { ...subscribedProducts },
          },
        },
      },
    });

    const refreshedTokenResult = await apolloClient.query({
      query: GET_TOKEN,
      variables: {
        userId: Number(providerUser?.userId),
        businessId,
        staffId: providerUser?.staffId ? Number(providerUser.staffId) : null,
        subscriptionId: providerUser?.subscriptionId ? Number(providerUser.subscriptionId) : null,
      },
      fetchPolicy: "network-only",
    });

    const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
    if (refreshedToken) {
      localStorage.setItem("token", refreshedToken);
      updateProviderUser(jwtDecode(refreshedToken));
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isLoggedIn) {
      localStorage.setItem("redirectAfterLogin", authReturnTo);
      setSeatToast({
        type: "info",
        message: "Please log in or sign up first to choose a plan.",
      });
      return;
    }

    try {
      const selectedPlan = availablePlans.find(
        (plan) => String(plan?.id) === String(planId)
      );
      const selectedTierCode = getPlanTierCode(selectedPlan?.code);
      if (selectedTierCode === "FREE") {
        await updateProductSubscription("booking", true);
        setSeatToast({
          type: "success",
          message: "Booking Free subscribed. It now appears in My Products.",
        });
        navigate("/booking");
        return;
      }

      const fromRoute = getInternalFromRoute();
      const upgradeCallerPath = getUpgradeCallerPath();
      const lastBizRoute = getLastBizRouteBeforePricing();
      const fallbackPath = `${location.pathname}${location.search}${location.hash}`;
      const candidatePath = upgradeCallerPath || fromRoute || fallbackPath;
      const returnPath = isPricingRoute(candidatePath)
        ? (lastBizRoute || "/biz/business")
        : candidatePath;

      if (returnPath) {
        localStorage.setItem(
          "pendingUpgradeNavigation",
          JSON.stringify({
            path: returnPath,
            businessId: Number(businessId || 0),
            createdAt: Date.now(),
          })
        );
      }

      const res = await createSubscription({
        variables: {
          businessId,
          planId,
          residentCount: isPersonalWorkspace ? 1 : desiredSeats,
          productKey: productContext,
        },
      });

      const checkoutUrl = res?.data?.createStripeSubscription?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      localStorage.removeItem("pendingUpgradeNavigation");
      await refetch();
      setSeatToast({
        type: "success",
        message: "Plan updated.",
      });
    } catch (err) {
      localStorage.removeItem("pendingUpgradeNavigation");
      setSeatToast({
        type: "error",
        message: err?.message || "Failed to update plan.",
      });
    }
  };

  const handleChangeSeats = async (newSeats) => {
    if (!isLoggedIn) {
      localStorage.setItem("redirectAfterLogin", authReturnTo);
      setSeatToast({
        type: "info",
        message: "Please log in or sign up first to manage plan seats.",
      });
      return;
    }

    if (!businessId) {
      setSeatToast({
        type: "error",
        message: "Missing workspace. Please refresh and try again.",
      });
      return;
    }
    if (newSeats < minSeatsAllowed) {
      setSeatToast({
        type: "error",
        message: `You cannot set fewer than ${minSeatsAllowed} workspace members.`,
      });
      return;
    }

    try {
      setUpdatingSeats(true);
      await updateStripeSeats({
        variables: {
          businessId,
          seats: newSeats,
          productKey: productContext,
        },
      });
      await refetch();
      setSeatToast({
        type: "success",
        message:
          "Workspace member count updated.",
      });
    } catch (err) {
      console.error("Failed to update subscription quantity", err);
      setSeatToast({
        type: "error",
        message:
          err?.message ||
          ("Failed to update workspace member count."),
      });
    } finally {
      setUpdatingSeats(false);
    }
  };

  const handleTrialSeatControlClick = () => {
    setSeatToast({
      type: "info",
      message:
        isBusinessTrial
            ? "You are in a free Booking trial. Workspace member counts become editable after trial ends and you upgrade to a paid plan."
            : "Choose a paid Booking plan to start managing workspace member counts.",
    });
  };

  const handleProductSelectionChange = (event) => {
    const nextProduct = normalizeProductKey(event.target.value);
    if (!nextProduct || nextProduct === productContext) return;

    const params = new URLSearchParams(location.search);
    params.set("product", nextProduct);
    params.set("v", nextProduct);
    window.location.assign(`/biz/pricing?${params.toString()}`);
  };

  const handleBusinessSelectionChange = async (event) => {
    const nextBusinessId = Number(event.target.value || 0);
    if (!nextBusinessId) return;
    if (nextBusinessId === Number(businessId || 0)) return;

    dispatch(setSelectedBusiness(nextBusinessId));

    try {
      await updateDefaultBusiness({
        variables: { defaultBusinessId: nextBusinessId },
      });

      const refreshedTokenResult = await apolloClient.query({
        query: GET_TOKEN,
        variables: {
          userId: Number(providerUser?.userId),
          businessId: nextBusinessId,
          staffId: null,
          subscriptionId: null,
        },
        fetchPolicy: "network-only",
      });

      const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
      if (refreshedToken) {
        localStorage.setItem("token", refreshedToken);
        const decoded = jwtDecode(refreshedToken);
        updateProviderUser(decoded);
        const decodedDefaultBusinessId = Number(decoded?.defaultBusinessId || 0);
        if (decodedDefaultBusinessId > 0) {
          dispatch(setSelectedBusiness(decodedDefaultBusinessId));
        }
      }
    } catch (error) {
      console.error("Failed to switch business context from pricing:", error);
      setSeatToast({
        type: "error",
        message: "Unable to switch workspace. Please try again.",
      });
      const fallbackBusinessId = Number(defaultBusinessId || businessId || 0);
      if (fallbackBusinessId > 0) {
        dispatch(setSelectedBusiness(fallbackBusinessId));
      }
    }
  };

  const getInternalFromRoute = () => {
    const from = location?.state?.from;
    if (!from || typeof from !== "string") return "";

    if (from.startsWith("/")) return from;

    if (/^https?:\/\//i.test(from)) {
      try {
        const parsed = new URL(from);
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        return "";
      }
    }

    return "";
  };

  const getLastBizRouteBeforePricing = () => {
    if (typeof window === "undefined") return "";
    try {
      const stored = localStorage.getItem("lastBizRouteBeforePricing") || "";
      if (!stored || isPricingRoute(stored)) return "";
      return stored;
    } catch (_error) {
      return "";
    }
  };

  const handleClose = () => {
    const returnPath = getPricingClosePath(getInternalFromRoute() || getUpgradeCallerPath() || getLastBizRouteBeforePricing());
    try {
      localStorage.removeItem("pendingUpgradeNavigation");
      localStorage.removeItem("upgradeCallerNavigation");
      localStorage.removeItem("lastBizRouteBeforePricing");
    } catch (_error) {
      // Closing must still work if browser storage is unavailable.
    }
    navigate(returnPath, { replace: true });
  };
  /* ================= RENDER ================= */

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-container": shouldReserveDockedBotSpace
          ? {
            justifyContent: "flex-start",
            px: 2,
            pr: `calc(${inlineBotOccupiedWidth}px + 16px)`,
          }
          : undefined,
        "& .MuiDialog-paper": shouldReserveDockedBotSpace
          ? {
            width: `calc(100vw - ${inlineBotOccupiedWidth}px - 32px)`,
            maxWidth: 1200,
            ml: 0,
            mr: 0,
          }
          : undefined,
      }}
    >
      <Box sx={{ p: 4, backgroundColor: "background.default", color: "text.primary" }}>
        {subscription && <Box sx={{ mb: 2 }}><Button disabled={openingPortal} onClick={async () => {
          try { const result = await openBillingPortal({ variables: { businessId } }); window.location.assign(result.data.createStripeBillingPortal); } catch (_) {}
        }}>Manage billing and invoices</Button>{portalError && <Typography color="error">{portalError.message}</Typography>}</Box>}

        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          align="center"
          variant="overline"
          sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.08em" }}
        >
          {pricingEyebrow}
        </Typography>

        <Typography
          align="center"
          variant={isMobile ? "h4" : "h3"}
          fontWeight={800}
        >
          {pricingTitle}
        </Typography>

        <Typography
          align="center"
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          {pricingDescription}
        </Typography>
        <Typography
          align="center"
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          {pricingFootnote}
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >

          {!isCurrentProductSubscribed && (
            <Button variant="outlined" onClick={() => setOpenDemoDialog(true)}>
              Request a Demo
            </Button>
          )}
        </Box>

        {!isLoggedIn && (
          <Alert
            severity="info"
            sx={{
              mt: 3,
              alignItems: "center",
              "& .MuiAlert-message": { width: "100%" },
            }}
            action={
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  component="a"
                  href={loginPath}
                  color="inherit"
                  size="small"
                  variant="outlined"
                >
                  Log In
                </Button>
                <Button
                  component="a"
                  href={signupPath}
                  color="inherit"
                  size="small"
                  variant="contained"
                >
                  Sign Up
                </Button>
              </Stack>
            }
          >
            You can view pricing now. Log in or sign up first to choose a plan.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Each personal or business workspace has its own plan and billing. Choose Standard or Premium separately for each workspace.
          Where listed, member charges apply to active members within that workspace, including the owner.
        </Typography>

        {Array.isArray(businesses) && businesses.length > 0 && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", md: 360 }, backgroundColor: "background.paper" }}
            >
              <InputLabel id="pricing-business-label">Workspace</InputLabel>
              <Select
                labelId="pricing-business-label"
                value={businessId || ""}
                label="Workspace"
                onChange={handleBusinessSelectionChange}
              >
                {businesses.map((business) => (
                  <MenuItem
                    key={business.businessBasic.id}
                    value={Number(business.businessBasic.id)}
                  >
                    {business.businessBasic.name} ({business.businessBasic.workspaceType === "PERSONAL" ? "Personal" : "Business"})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              {selectedBusinessName
                ? `${selectedBusinessName}${isShowingCurrentDefaultBusiness ? " (current workspace)" : ""}`
                : "Select a workspace to view its plan and member charges."}
            </Typography>
          </Box>
        )}

        {!pricingCatalogLoading && hasPaidPlans && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {isPersonalWorkspace ? "Personal workspace · Just you" : quantityPanelTitle}
              {isBusinessTrial ? (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.8,
                    py: 0.15,
                    borderRadius: 999,
                    fontSize: "0.76rem",
                    fontWeight: 700,
                    color: theme => theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
                    backgroundColor: theme => alpha(theme.palette.warning.main, 0.15),
                    verticalAlign: "middle",
                  }}
                >
                  [Trial]
                </Box>
              ) : null}
            </Typography>

            {!isPersonalWorkspace && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {canPersistSeatChanges || isBusinessTrial
                ? `Current subscription: ${displayedSeatLimit} purchased, ${usedSeats} ${quantityUsedLabel}, ${displayedSeatsLeft} left.`
                : isCurrentPaidPlan
                  ? `Choose ${quantityUnitLabel} quantity once and use it for paid plan checkout.`
                  : `Free or no-subscription workspaces have 0 ${zeroQuantityLabel} until you upgrade to a paid plan.`}
            </Typography>}

            {isCanceled && !isBusinessTrial && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                Your subscription is canceled. Selecting a paid plan starts a new checkout.
              </Typography>
            )}
            {isTrialing && nextBillingDateLabel && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                Trial active until {nextBillingDateLabel}.
              </Typography>
            )}
            {!isTrialing && isBusinessTrial && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                {trialDaysLeft === "Unlimited"
                  ? "Workspace trial is active with unlimited time remaining."
                  : `Workspace trial is active with ${trialDaysLeft} day${Number(trialDaysLeft) === 1 ? "" : "s"} left.`}
              </Typography>
            )}
            {!isPersonalWorkspace && disableSeatControlsForTrial && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                {"You are in a free trial period. Use Booking features during trial. Workspace member counts become editable after trial ends and you upgrade to a paid plan."}
              </Typography>
            )}

            {!isPersonalWorkspace && ((((canManageCurrentSeats || isCurrentPaidPlan) && !isBusinessTrial) || productContext === "booking") ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    disabled={
                      !disableSeatControlsForTrial &&
                      (desiredSeats <= MIN_INITIAL_SEATS || updatingSeats)
                    }
                    onClick={() =>
                      (disableSeatControlsForTrial || productContext === "booking")
                        ? handleTrialSeatControlClick()
                        : setDesiredSeats((seats) =>
                          Math.max(MIN_INITIAL_SEATS, seats - 1)
                        )
                    }
                    sx={
                      (disableSeatControlsForTrial || productContext === "booking")
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    -
                  </Button>

                  <Typography fontWeight={700} minWidth={24} textAlign="center">
                    {displayedSeatLimit}
                  </Typography>

                  <Button
                    variant="outlined"
                    disabled={!(disableSeatControlsForTrial || productContext === "booking") && updatingSeats}
                    onClick={() =>
                      (disableSeatControlsForTrial || productContext === "booking")
                        ? handleTrialSeatControlClick()
                        : setDesiredSeats((seats) => seats + 1)
                    }
                    sx={
                      (disableSeatControlsForTrial || productContext === "booking")
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    +
                  </Button>

                  {updatingSeats && <CircularProgress size={20} />}
                </Box>

                {canPersistSeatChanges && desiredSeats !== paidSeats ? (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 1.5 }}
                    onClick={() => handleChangeSeats(desiredSeats)}
                    disabled={
                      updatingSeats ||
                      !businessId ||
                      desiredSeats < minSeatsAllowed
                    }
                  >
                    {updatingSeats ? (
                      <CircularProgress size={22} />
                    ) : "Update Member Count"}
                  </Button>
                ) : null}
              </>
            ) : isBusinessTrial ? (
              <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
                {`Current active workspace members: ${usedSeats}`}
              </Typography>
            ) : (
              <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
                {`Current ${zeroQuantityLabel}: 0`}
              </Typography>
            ))}
          </Box>
        )}

        {pricingCatalogLoading ? (
          <Box
            sx={{
              mt: 4,
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading pricing plans...
            </Typography>
          </Box>
        ) : hasConfigurablePricing ? (
          <GridLayout>
            {availablePlans.map((plan) => {
              const tierCode = getPlanTierCode(plan.code);
              const isFreePlan = tierCode === "FREE";
              const isPaidPlan = !isFreePlan;
              const isContactOnlyEnterprise =
                false;
              const isCurrentPlan =
                Boolean(currentPlanId) && String(plan.id) === String(currentPlanId);

              const seatCount = isPersonalWorkspace ? 1 : isCurrentPlan
                ? displayedSeatLimit
                : isBusinessTrial
                  ? Math.max(1, displayedSeatLimit || 0)
                  : Math.max(1, desiredSeats || 0);
              const baseMonthly = Number(
                plan?.pricing?.baseMonthly
              );
              const perUnitMonthly = Number(
                plan?.pricing?.perUnitMonthly ??
                (plan?.pricing?.perStaffMonthly)
              );
              const hasBaseMonthly = Number.isFinite(baseMonthly);
              const hasPerUnitMonthly = Number.isFinite(perUnitMonthly);
              const estimatedTotal =
                hasBaseMonthly && hasPerUnitMonthly
                  ? baseMonthly + seatCount * perUnitMonthly
                  : null;
              const planSummary = String(plan?.description || "").trim();
              const hidePlanSummary = shouldHidePlanSummary(plan);
              const planFeatures = normalizeFeatures(plan.features);
              const effectivePlanFeatures = {
                  ...planFeatures,
                  capture: [
                    ...(Array.isArray(planFeatures.capture) ? planFeatures.capture : []),
                    ...getBookingCaptureFeatures(tierCode),
                  ],
                };
              const visibleFeatureEntries = getOrderedFeatureEntries(effectivePlanFeatures)
                .map(([key, items]) => [
                  key,
                  (Array.isArray(items) ? items : [])
                    .map((item) => formatFeatureText(item))
                    .filter(Boolean),
                ])
                .filter(([, items]) => items.length > 0);

              return (
                <Card key={plan.id} $planCode={plan.code} $currentPlan={isCurrentPlan}>
                  <Typography variant="h5" fontWeight={700}>
                    {getDisplayedPlanName(plan, productContext)}
                  </Typography>

                  {!hidePlanSummary ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {planSummary || "Plan details are managed in the billing catalog."}
                    </Typography>
                  ) : null}

                  {isPaidPlan && (
                    <>
                      {hasBaseMonthly && !isContactOnlyEnterprise ? (
                        <Typography sx={{ mt: 2 }}>
                          <strong>${baseMonthly}</strong> per workspace / month
                        </Typography>
                      ) : null}
                      {hasPerUnitMonthly && !isContactOnlyEnterprise ? (
                        <Typography sx={{ ml: 1 }}>
                          + <strong>${perUnitMonthly}</strong> per workspace member / month
                        </Typography>
                      ) : null}

                      <Divider sx={{ my: 1 }} />

                      {estimatedTotal != null ? (
                        <Typography variant="h6" fontWeight={800}>
                          Total ${estimatedTotal} / month
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Pricing details for this plan are managed by the backend catalog.
                        </Typography>
                      )}
                      {(hasBaseMonthly || hasPerUnitMonthly) && !isContactOnlyEnterprise ? (
                        <Typography variant="caption" color="text.secondary">
                          Billed monthly, tax not included.
                        </Typography>
                      ) : null}

                      {isCurrentPlan ? (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            disabled
                            sx={{ fontWeight: 800, letterSpacing: 0.2 }}
                          >
                            {isCanceling ? "Canceling" : "Current Plan"}
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={subscribing || (isLoggedIn && !businessId)}
                            onClick={() => handleSubscribe(plan.id)}
                          >
                            {subscribing ? (
                              <CircularProgress size={22} />
                            ) : (
                              isLoggedIn
                                ? getPlanActionLabel(currentPlanCode, tierCode)
                                : "Log In or Sign Up"
                            )}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}

                  {isFreePlan && (
                    <Box sx={{ mt: 2 }}>
                      {isCurrentPlan ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled
                          sx={{ fontWeight: 800, letterSpacing: 0.2 }}
                        >
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={subscribing || (isLoggedIn && !businessId)}
                          onClick={() => handleSubscribe(plan.id)}
                        >
                          {subscribing ? (
                            <CircularProgress size={22} />
                          ) : (
                            isLoggedIn ? "Switch to Free" : "Log In or Sign Up"
                          )}
                        </Button>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {visibleFeatureEntries.length > 0 ? visibleFeatureEntries.map(([key, items], index) => (
                    <Accordion
                      key={`${plan.id}-${key}`}
                      disableGutters
                      elevation={0}
                      defaultExpanded={index === 0}
                      sx={{
                        backgroundColor: "transparent",
                        backgroundImage: "none",
                        "&:before": { display: "none" },
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small" />}
                        sx={{ minHeight: 40 }}
                      >
                        <SectionTitle sx={{ mt: 0 }}>
                          {FEATURE_LABELS[key] || key}
                        </SectionTitle>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        <Box component="ul" sx={{ pl: 0, mb: 0 }}>
                          {(items || []).map((item, i) => (
                            <FeatureItem key={`${plan.id}-${key}-${i}`}>
                              <CheckIcon fontSize="small" color="success" />
                              <Typography variant="body2">{formatFeatureText(item)}</Typography>
                            </FeatureItem>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No feature list is configured for this plan yet.
                    </Typography>
                  )}

                </Card>
              );
            })}
          </GridLayout>
        ) : (
          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="h6" fontWeight={800}>
              {"Booking pricing is being prepared"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {"The reusable Stripe flow is ready for Booking, but there are no Booking plans configured in the billing catalog yet. Once Booking plans are added on the backend, they will appear here automatically."}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
              {!isCurrentProductSubscribed && (
                <Button variant="contained" onClick={() => setOpenDemoDialog(true)}>
                  Talk to Sales
                </Button>
              )}
              <Button variant="outlined" onClick={handleClose}>
                Back to Workspace
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!seatToast}
        autoHideDuration={4000}
        onClose={() => setSeatToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSeatToast(null)}
          severity={seatToast?.type || "info"}
          variant="filled"
        >
          {seatToast?.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDemoDialog}
        onClose={() => setOpenDemoDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">
            {isCurrentProductSubscribed ? "Contact Sales" : "Request a Demo"}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <ContactUsForm
            initialName={providerUser?.username || ""}
            initialFacility={selectedBusinessName || ""}
            initialEmail={providerUser?.email || ""}
            initialReason={isCurrentProductSubscribed ? "sales" : "demo"}
            initialSubject={isCurrentProductSubscribed ? "Plan Consultation" : "Request a Demo"}
            initialMessage={
              isCurrentProductSubscribed
                ? `I would like to discuss plan options for ${selectedBusinessName || "my workspace"}. Please contact me.`
                : `I would like a demo for ${selectedBusinessName || "my workspace"}. Please contact me to schedule a walkthrough.`
            }
          />
        </Box>
      </Dialog>
    </Dialog>
  );
}
